// ============================================================================
// 추적성 API 서비스 (CHG-047)
// ============================================================================

import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export interface TracePR { prId: string; prNumber: number; status: string; commitCount: number; additions: number; deletions: number }
export interface TraceTestCase { testCaseId: string; title: string; lastResult: 'PASS' | 'FAIL' | 'NOT_RUN' | null }
export interface TraceIssue {
  issueId: string; issueKey: string; title: string; status: string;
  prs: TracePR[];
  testCases: TraceTestCase[];
}
export interface TraceRequirement {
  requirementId: string; reqId: string; title: string; status: string;
  issues: TraceIssue[];
  traceStatus: 'COMPLETE' | 'PARTIAL' | 'NO_CODE' | 'NO_TEST' | 'NO_ISSUE';
}
export interface TraceMatrix {
  summary: {
    totalRequirements: number; linkedToIssues: number; codeImplemented: number;
    testCovered: number; fullyTraced: number; coveragePercent: number;
  };
  requirements: TraceRequirement[];
  page: number; limit: number; totalPages: number;
}

export interface IssueTraceChain {
  issue: { issueId: string; issueKey: string; title: string; status: string; statusColor: string };
  upstream: { requirement: { requirementId: string; reqId: string; title: string; status: string } | null };
  downstream: {
    branches: { branchId: string; name: string; status: string }[];
    pullRequests: { prId: string; prNumber: number; title: string; status: string; githubUrl: string }[];
    commits: { commitId: string; sha: string; shortSha: string; message: string; linkType: string }[];
    testCases: { testCaseId: string; tcId: string; title: string; lastResult: string | null }[];
  };
}

export interface RequirementTraceChain {
  requirement: { requirementId: string; reqId: string; title: string; status: string };
  issues: {
    issueId: string; issueKey: string; title: string; status: string; statusColor: string;
    branches: { branchId: string; name: string; status: string }[];
    pullRequests: { prId: string; prNumber: number; title: string; status: string; githubUrl: string }[];
    commits: { commitId: string; sha: string; shortSha: string; message: string; linkType: string }[];
    testCases: TraceTestCase[];
  }[];
}

export async function fetchTraceabilityMatrix(projectId: string, params?: Record<string, string>): Promise<TraceMatrix> {
  const res = await api.get<ApiResponse<TraceMatrix>>(`/projects/${projectId}/traceability`, { params });
  return res.data.data!;
}

export async function fetchIssueTraceChain(projectId: string, issueId: string): Promise<IssueTraceChain> {
  const res = await api.get<ApiResponse<IssueTraceChain>>(`/projects/${projectId}/traceability/issues/${issueId}/chain`);
  return res.data.data!;
}

export async function fetchRequirementTraceChain(projectId: string, reqId: string): Promise<RequirementTraceChain> {
  const res = await api.get<ApiResponse<RequirementTraceChain>>(`/projects/${projectId}/traceability/requirements/${reqId}`);
  return res.data.data!;
}
